// Pure questions about a room's current state -- "can the player stand here",
// "is this pad satisfied", "where is the water flowing". Every one of these is a
// function of a RoomDef plus arguments: no Phaser, no sprites, no scene, no
// mutation. They were GameScene methods reading `this.rooms[this.currentRoomId]`
// (2026-07-30, sim extraction step 1c); GameScene now delegates to them so every
// call site is unchanged.
//
// Unlike steps 1a/1b, this file is not a byte-identical move -- `isWalkable` had
// to be rewritten, see its own comment. Everything else is the original body with
// `this.rooms[this.currentRoomId]` replaced by an explicit `room` parameter.
import { DoorDef, Facing, FluidId, GateCondition, GateDef, ObjectDef, RoomDef } from './types';
import { CLIFF_EAST, CLIFF_NORTH, DIRECTIONS, DIRECTION_OFFSETS, VOID, WALL } from './constants';
import { DEFAULT_PHYSICS, PhysicsConfig } from './physics';
import { vineClimbable } from './vines';
import { rustStage } from './rust';

// Shared by applyMomentumEffect's caster recoil and applyMomentumProject's
// tractor-beam pull, both of which need "whichever direction undoes this
// one" rather than hardcoding it inline twice.
export function oppositeFacing(dir: Facing): Facing {
  return dir === 'right' ? 'left' : dir === 'left' ? 'right' : dir === 'down' ? 'up' : 'down';
}

// Steps up to `maxTiles` in `dir` from (startX, startY), one tile at a time,
// stopping the moment `isBlocked` says the next tile isn't available -- shared
// by applyMomentumEffect's block-push and caster-recoil paths, which use the
// same "go as far as you can, then stop" rule in opposite directions.
export function stepPath(
  startX: number,
  startY: number,
  dir: Facing,
  maxTiles: number,
  isBlocked: (x: number, y: number) => boolean,
): { x: number; y: number; steps: number } {
  const offset = DIRECTION_OFFSETS[dir];
  let x = startX;
  let y = startY;
  let steps = 0;
  while (steps < maxTiles) {
    const nx = x + offset.x;
    const ny = y + offset.y;
    if (isBlocked(nx, ny)) break;
    x = nx;
    y = ny;
    steps++;
  }
  return { x, y, steps };
}

export function doorAt(room: RoomDef, x: number, y: number): DoorDef | undefined {
  return room.doors.find((d) => d.x === x && d.y === y);
}

// Shared by checkMeltThreshold and isWalkable's reformable-barrier check --
// "every meltsWhen rule currently satisfied", independent of whether that
// should permanently remove the object or just make it passable for now.
export function meltRulesSatisfied(obj: ObjectDef): boolean {
  const rules = obj.barrier?.meltsWhen;
  if (!rules || rules.length === 0) return false;
  return rules.every((rule) => {
    const value = obj.properties?.[rule.concept] ?? 0;
    return rule.aboveOrBelow === 'above' ? value >= rule.threshold : value <= rule.threshold;
  });
}

// The one function in this file that isn't a verbatim move. It used to read the
// live Phaser tilemap (`this.layer.getTileAt(x, y)`, then `tile.collides`), which
// is what tied the most fundamental predicate in the game to a rendered scene.
// The tilemap is built directly from `room.data` and configured with exactly
// `layer.setCollision(WALL)` (see GameScene's enterRoom), so the two formulations
// are equivalent case for case:
//   - out of bounds -> getTileAt returned null -> blocked; here data[y]?.[x] is
//     undefined -> blocked (negative indices included, since arrays return
//     undefined rather than wrapping)
//   - tile id WALL  -> tile.collides was true -> blocked; here === WALL -> blocked
//   - anything else (FLOOR, DOOR, every FLOOR_ROOM*) -> passable in both
// Object occupancy below is unchanged from the original.
export function isWalkable(room: RoomDef, x: number, y: number, physics: PhysicsConfig = DEFAULT_PHYSICS): boolean {
  return tileIsFree(room, x, y, physics, true);
}

/** Whether an *object* can end up on this tile: everything `isWalkable` asks
 * except the climb.
 *
 * **Climbing is a deliberate act of walking, not something that happens to
 * you.** A Boulder shoved at a cliff face does not go up it because vines
 * happen to be growing there, and neither does a pulled block, a split piece,
 * a dragged bind partner, a recoiling caster or a running river. Every one of
 * those went through `isWalkable` before 2026-08-11 and so could climb, which
 * is how a block could be pushed up a vine -- a reported bug, and one that made
 * the vine ladder a shortcut for the Mass puzzles rather than a route for the
 * player.
 *
 * The caster's own step is the one movement that asks `isWalkable`, because it
 * is the only one anybody chooses to make. That is the whole distinction, and
 * it is why this is a second reading of one rule rather than a second rule. */
export function canObjectEnter(room: RoomDef, x: number, y: number, physics: PhysicsConfig = DEFAULT_PHYSICS): boolean {
  return tileIsFree(room, x, y, physics, false);
}

function tileIsFree(room: RoomDef, x: number, y: number, physics: PhysicsConfig, climbing: boolean): boolean {
  const tileId = room.data[y]?.[x];
  // VOID is not-part-of-the-room, and is impassable for exactly the reason
  // out-of-bounds is: there is no floor there. A room whose footprint is not a
  // rectangle is void wherever it is not room, so this is the same `undefined`
  // case spelled inside the grid.
  if (tileId === undefined || tileId === WALL || tileId === VOID) return false;
  // A cliff tile is impassable like WALL by default -- unless something is
  // climbing it (today, only Vines in a climbable stage) and whatever is
  // arriving can climb. Recomputed live on every call, the same as the
  // reformable check just below: there is no stored "this cliff is currently
  // climbed" flag, only the climbing object's own current state.
  if (tileId === CLIFF_NORTH || tileId === CLIFF_EAST) {
    if (!climbing) return false;
    const climber = room.objects.find((o) => o.x === x && o.y === y && o.vines);
    if (!climber || !vineClimbable(climber, physics)) return false;
  }
  const blockedByObject = room.objects.some((o) => {
    if (o.x !== x || o.y !== y) return false;
    if (o.walkable) return false;
    // A reformable barrier currently melted through is passable right now --
    // recomputed live every time, not a one-shot "it's gone" event, so it
    // blocks again the moment its properties decay back under threshold.
    if (o.barrier?.reformable && meltRulesSatisfied(o)) return false;
    // A gate that can be seen open is never removed from the room (see
    // GateDef), so its passability has to be read off its state here rather
    // than falling out of room.objects the way a one-way gate's does. Same
    // live-check shape as the reformable branch above -- the difference is
    // only what is being read: a persisted flag a trigger wrote, not a
    // threshold recomputed from properties.
    //
    // **A gate opens for whatever it is a gate over**, which is why a sluice is
    // excepted rather than special-cased: a gate standing in a doorway opens the
    // doorway, and a gate standing in a channel opens the channel. An open
    // Sluice Gate is a raised leaf with the water running under it, and walking
    // into the channel it just opened is exactly as impossible as it was before.
    if (o.gate && gateIsOpen(o.gate) && !isSluice(o)) return false;
    return true;
  });
  return !blockedByObject;
}

/** Every tile in the room that stops a straight line through it -- a wall, and
 * anything solid enough to stop a `Project` beam.
 *
 * Precomputed as a set because both callers walk many lines over the same room:
 * `isProjectPathBlocked` asks once per object a beam might reach, and
 * `sim/light.ts` asks once per tile per source. Walking `room.objects` at every
 * step of every line is what that would otherwise cost, and room16 has 173
 * objects. */
export function solidTiles(room: RoomDef): Set<string> {
  const solid = new Set<string>();
  for (const obj of room.objects) if (isSolidForProject(obj)) solid.add(`${obj.x},${obj.y}`);
  return solid;
}

/** Whether anything blocks the straight line from one tile to another --
 * **excluding both endpoints**.
 *
 * A standard Bresenham grid walk rather than a continuous raycast, since a
 * Linear projection travels tile to tile. Lifted here out of `casts.ts`'s
 * `isProjectPathBlocked` (2026-08-16) when light needed the same question asked
 * from somewhere other than the caster's feet: a room-level geometric question
 * belongs with the other room-level questions, and the alternative -- a second
 * walk in `sim/light.ts` -- is two opinions about what a wall is, which is
 * exactly the drift this file exists to prevent. `Project` and light now agree
 * about occlusion by construction, **including** the known artifact that a
 * diagonal line can slip past a lone wall tile (pinned in `sim.test.ts`): that is
 * already this world's behaviour for beams, so light inherits it rather than
 * introducing a second, differently-wrong shadow.
 *
 * The target tile is deliberately not checked, which is what makes an object
 * standing in the light lit even though it casts a shadow past itself -- the
 * same rule that lets a Project beam hit the nearest solid thing in its path. */
export function pathBlocked(room: RoomDef, fromX: number, fromY: number, toX: number, toY: number, solid: ReadonlySet<string> = solidTiles(room)): boolean {
  let x = fromX;
  let y = fromY;
  const dx = Math.abs(toX - x);
  const dy = -Math.abs(toY - y);
  const sx = x < toX ? 1 : x > toX ? -1 : 0;
  const sy = y < toY ? 1 : y > toY ? -1 : 0;
  let err = dx + dy;
  while (x !== toX || y !== toY) {
    const e2 = 2 * err;
    if (e2 >= dy) {
      err += dy;
      x += sx;
    }
    if (e2 <= dx) {
      err += dx;
      y += sy;
    }
    if (x === toX && y === toY) return false; // the target tile itself is not a path tile
    if (room.data[y]?.[x] === WALL) return true;
    if (solid.has(`${x},${y}`)) return true;
  }
  return false;
}

// A solid physical obstruction -- real mass (any `mass` cluster: Boulder/Rock/
// Pebble/Warded Stone, and every Metal/Wood Block) is always solid, implicitly,
// without needing `blocksProject` set on every single one (including runtime
// split/merge pieces, which always set `mass` too); everything else that should
// stop a beam -- room5's Stone Block, an Ice Block, a Sand Block -- opts in
// explicitly via `blocksProject`. The two overlap harmlessly: an authored
// object may set both, and the shipped Metal Blocks do.
export function isSolidForProject(o: ObjectDef): boolean {
  return o.blocksProject === true || o.mass !== undefined;
}

/** Whether an object is currently a *liquid*, whatever its authored silhouette
 * says. Today that is exactly the `sand` ladder's molten stage -- a slumped
 * mass of glowing glass, which the ladder already tells you is in thermal
 * contact with whatever touches it.
 *
 * Read as data (`sand.stage`) rather than through `sim/sand.ts`, deliberately:
 * `queries.ts` is imported by that module's own neighbours and stays free of
 * every mechanism module for the same reason `vines.ts` refuses to import
 * `mechanics.ts` -- see that file's own note on the cycle it would close. */
function isMolten(o: ObjectDef): boolean {
  return o.sand?.stage === 'molten';
}

// Whether a pushable object can currently be shoved by anything -- an
// ordinary walk-into-push, Transfer·Momentum, or Project·Momentum alike.
//
// A `bind`-linked object is excluded: its own movement is already entirely
// dictated by whatever locus it follows (see casts.ts's propagateBoundMovement),
// so an independent shove would double-count with that link rather than
// meaning anything of its own -- the same reasoning that makes "push a bound
// object" nonsensical everywhere the game currently offers to push one.
// This does not make a bound-to-caster object un-walk-into-able, though --
// see boundObjectMove just below, which GameScene.tryMove uses instead for
// exactly that case: not a push, but the object following the caster's own
// step the same way it would follow any other.
//
// A molten object is excluded for a plainer reason (2026-08-11, reported): you
// cannot shove a puddle. A Sand Block heated past `physics.sand.meltEnergy` is
// a slumped mass of molten glass that says so on inspection, burns what stands
// on it, and was still being pushed around the room a tile at a time like the
// solid block it stopped being. Melting it is therefore a real, reversible cost
// -- park a block somewhere by melting it, cool it back to glass to move it
// again -- rather than a free reskin, which is what a phase change ought to be
// worth. Deliberately checked here rather than by clearing `pushable` on the
// object: `pushable` is authored, and a ladder that overwrote it would have
// nothing to restore when the melt cooled (the Sand *Floor* variant is not
// pushable at any stage, and must not become so by being heated and cooled).
export function canPush(o: ObjectDef): boolean {
  return !!o.pushable && !o.bind && !isMolten(o);
}

// Where a shove aimed at `obj` actually has to land, now that a bind-linked
// object can be pushed too -- not by moving *it* directly (still exactly as
// nonsensical as canPush's own comment says, since its position is entirely
// dictated by whatever it follows), but by redirecting the shove onto its
// own bind *source* instead: pushing the source moves it via the ordinary
// propagateBoundMovement path, which naturally drags the original object
// along right back to where a direct push would have wanted it to land. A
// real, reported bug before this existed: a Bind·Momentum link only ever let
// you push one end of it, when a real physical link should carry force
// either way.
//
// Deliberately narrow, matching Bind's own existing guarantees elsewhere:
//   - Only redirects through a link to a *marked object* source -- an object
//     bound to the caster (`sourceId` unset) is left alone; GameScene's own
//     `boundAhead` walk-into path already covers that case (following, not
//     being pushed), and "pushing something drags the caster" is a much
//     bigger step this fix does not take.
//   - One hop only, never chased further: if the resolved source is itself
//     bound to something else, this refuses (null) rather than chaining --
//     the same "one link deep, not transitive" rule propagateBoundMovement's
//     own doc comment already commits to, so a redirected push can't end up
//     walking an arbitrarily long (or cyclic) chain of links.
//   - `mirrored` is preserved on the returned object so the caller can flip
//     the direction it pushes the source in: since a mirrored follower moves
//     opposite its source's own delta, moving it in the direction the player
//     actually pushed *it* requires moving the source the other way.
//   - A molten object is no more shoveable through a bind link than directly
//     (see canPush), at either end of it: neither a puddle nor whatever is
//     dragging one has anything solid to push against.
export function pushTargetFor(room: RoomDef, obj: ObjectDef): { target: ObjectDef; mirrored: boolean } | null {
  if (!obj.pushable || isMolten(obj)) return null;
  if (!obj.bind) return { target: obj, mirrored: false };
  if (!obj.bind.sourceId) return null;
  const source = room.objects.find((o) => o.id === obj.bind!.sourceId);
  if (!source?.pushable || source.bind || isMolten(source)) return null;
  return { target: source, mirrored: !!obj.bind.mirrored };
}

// Where a bound object would land if its locus (whatever it's bound to)
// moves by (dx, dy) -- or null if it cannot move at all. Pure: mutates
// nothing. Walks the path one tile at a time, same shape as stepPath, rather
// than checking only the final tile -- (dx, dy) can be more than one tile (an
// Unbound Momentum push, or the recoil it gives the caster), and a plain
// endpoint-only check let a dragged object's *path* cross tiles a step-by-step
// push would have stopped at, including -- a real, reported bug -- straight
// through the caster's own tile on a Reverse-bound (mirrored) link, which
// moves opposite its locus's direction and so, unlike an ordinary push
// (always moving an object away from whoever's pushing it), can double back
// through wherever the caster happens to be standing. Stops at the first
// blocked tile and returns however far it actually got (never null once it
// has moved at least one tile), the same partial-drag-on-obstruction
// behavior stepPath already gives an ordinary push. Blocked by: a wall, a
// door, another solid object (isWalkable's own ordinary check), the locus's
// own tile (needed because the caster, when self is the locus, is never one
// of room.objects for isWalkable to already exclude), or the caster's own
// tile when the locus is a *different*, marked object (playerX/playerY,
// optional so a call with no reason to care -- nothing here is Reverse-
// bound, or the locus already covers it -- doesn't have to thread
// coordinates it doesn't need). Two callers share this exact computation and
// must agree on the answer, since one decides whether a move should be
// *allowed* before it happens and the other actually applies it afterward:
//   - GameScene.tryMove, before committing to a step, to decide whether
//     walking straight into a bound-to-caster object should succeed (the
//     object gets dragged out of the way in the same step, exactly the way
//     it would follow any other step) or be blocked outright (its own
//     destination is unavailable, so there is nowhere for it to go). Always
//     a single-tile (dx, dy) there, since an ordinary walk is always one
//     tile, and the locus passed already *is* the caster's own (prospective)
//     tile, so playerX/playerY is omitted.
//   - casts.ts's propagateBoundMovement, after the locus has already moved,
//     to actually relocate every object bound to it -- here (dx, dy) can be
//     multi-tile, and the locus can be a marked object entirely independent
//     of where the caster happens to be standing.
export function boundObjectMove(
  room: RoomDef,
  obj: ObjectDef,
  locusX: number,
  locusY: number,
  dx: number,
  dy: number,
  physics: PhysicsConfig = DEFAULT_PHYSICS,
  playerX?: number,
  playerY?: number,
): { x: number; y: number } | null {
  const sign = obj.bind?.mirrored ? -1 : 1;
  // dx/dy are always axis-aligned (this game has no diagonal movement), so
  // exactly one of them is nonzero -- the sum of their magnitudes is the
  // step count along whichever single direction that is.
  const steps = Math.abs(dx) + Math.abs(dy);
  const stepX = Math.sign(dx) * sign;
  const stepY = Math.sign(dy) * sign;
  // The tile the locus started this move on. Only a *mirrored* link is ever
  // blocked by it, and the asymmetry is the whole point: a same-direction
  // follower moves exactly in step with its locus, so its offset can never
  // change and it can never collide with it -- and the tile the locus just
  // vacated is precisely where such a follower is *supposed* to go when it is
  // trailing directly behind. A mirrored follower moves the opposite way, so
  // it and its locus close head-on at twice walking speed, and this tile is
  // the one they would swap through. Without this, a Reverse-bound object
  // adjacent to the caster traded places with them on every step -- the two
  // visibly passing through each other, a real reported bug. Landing *on* the
  // locus (rather than past it) was already covered by the locusX/locusY
  // check below; this closes the case where it overshoots by exactly one.
  const locusFromX = locusX - dx;
  const locusFromY = locusY - dy;
  let x = obj.x;
  let y = obj.y;
  let moved = 0;
  for (let i = 0; i < steps; i++) {
    const nx = x + stepX;
    const ny = y + stepY;
    if (nx === locusX && ny === locusY) break;
    if (sign === -1 && nx === locusFromX && ny === locusFromY) break;
    if (nx === playerX && ny === playerY) break;
    if (!canObjectEnter(room, nx, ny, physics) || doorAt(room, nx, ny)) break;
    x = nx;
    y = ny;
    moved++;
  }
  return moved > 0 ? { x, y } : null;
}

/** Where the caster is standing, for the live queries that read it.
 *
 * Deliberately not folded into `Caster` (which is `{ charge }`, what the
 * charge-network walk needs): the two answer different questions about the same
 * entity, and a single grab-bag with everything optional would let a caller pass
 * a charge-only value to something that silently wanted a position. `world.player`
 * structurally satisfies both, which is all any real call site needs. */
export interface CasterTile {
  x: number;
  y: number;
}

// room8's pressure pads: satisfied whenever whatever currently occupies the
// pad's tile matches what the pad accepts. Checked live (no stored state of its
// own), the same "recompute from current room" approach isWalkable already uses
// for reformable barriers.
//
// Each PadDef flag is one accepted occupant and they OR together, so a pad with
// no flags at all accepts nothing rather than accepting everything -- see
// PadDef on why that used to be the other way round by accident.
//
// `caster` is where the player stands, needed only by `forPlayer` and passed
// down from `settleWorld` (which is the only thing that knows). Omitting it
// makes a player pad read unsatisfied rather than throwing: a query run against
// a bare RoomDef with no world behind it -- the editor's palette preview, a
// unit test on a room fixture -- has no caster to speak of, and "nobody is
// standing on it" is the honest answer there.
export function isPadSatisfied(room: RoomDef, pad: ObjectDef, caster?: CasterTile): boolean {
  const accepts = pad.pad;
  if (!accepts) return false;
  if (accepts.forPlayer && caster && caster.x === pad.x && caster.y === pad.y) return true;
  if (!accepts.forAnyPushable && accepts.forRockTier === undefined) return false;
  return room.objects.some((o) => {
    if (o.id === pad.id || o.x !== pad.x || o.y !== pad.y) return false;
    if (accepts.forAnyPushable && o.pushable) return true;
    return accepts.forRockTier !== undefined && o.mass?.tier === accepts.forRockTier;
  });
}

// Whether an object's `conduit.conductive` flag still holds live -- a rusted
// metal object (see sim/rust.ts) stops conducting even though the flag itself
// is never cleared, the same "recomputed live" shape vineClimbable already
// uses for vines' own stage. An object with no `rust` cluster at all (every
// wood block, and any test fixture that never opted in) simply can't rust, so
// its `conductive` flag is the whole answer.
//
// **An open breaker is the second way to stop conducting**, and it is
// deliberately the same shape as the first: one line here, and the whole rest of
// the Charge system follows with no further rule -- the network walk stops at
// it, a `conduit.feed.pathTiles` path reads broken, a cable draws the run as
// ending there, and every reader beyond it goes dark. See `BreakerDef`.
export function isConductive(obj: ObjectDef, physics: PhysicsConfig = DEFAULT_PHYSICS): boolean {
  if (!obj.conduit?.conductive) return false;
  if (obj.breaker?.open) return false;
  return !obj.rust || rustStage(obj, physics) !== 'rusted';
}

/** Whether this object is currently **wrecked** -- it carries a collapse, and
 * its own rule currently says so.
 *
 * Both halves are load-bearing, and `meltRulesSatisfied` alone is not the same
 * question. A rule holding means "this object's threshold is crossed", and what
 * that *does* depends on which resolution the object carries: `collapsesInto`
 * wrecks it, `transformsInto` toggles it to its other face. room9's Aqueduct is
 * the case that makes the distinction: its rule reads satisfied exactly when it
 * is **restored**, so anything asking `meltRulesSatisfied` to mean "broken"
 * would have a mended aqueduct as the broken one.
 *
 * Live rather than a persisted flag, the same way `isConduitCharged` and
 * `isPadSatisfied` are: draining the Entropy back off a seized Water Wheel puts
 * it straight back to working, and a second copy of that fact could only
 * disagree with this one. */
export function isWrecked(obj: ObjectDef): boolean {
  return obj.barrier?.collapsesInto !== undefined && meltRulesSatisfied(obj);
}

// Everything that reads a charge level and lights up: room8/room9's gate
// pillars, and the consumers (Gate Terminal, Electric Lamp) added later.
// "Charged" whenever the potential at whatever this feeds from is at or above
// threshold, AND every tile along its conduction path currently holds a
// conductive (metal, not wood, not rusted) object. Checked live, the same
// "recompute from current room.objects" approach isPadSatisfied already uses --
// swapping a block in or out of the path (or rusting the one already there)
// takes effect immediately, no separate cast needed at the pillar itself (which
// has no properties of its own to cast at in the first place).
//
// **Both halves of a feed are optional, and that is the whole difference
// between the two shapes.** A feed naming a `standingPillarId` reads *that*
// object's network -- right for a pillar authored once for a specific puzzle,
// wrong for anything freely placeable, which is why Metal Floor stopped using
// this in 2026-08-03 after a room-editor bug report (a stamped tile pointed at
// whatever pillar id it happened to copy, which the new room does not have, and
// the loader refuses it). A feed with **no** named source reads its *own*
// network instead, which says the same thing about a consumer wired into a
// circuit -- "is there enough potential here" -- with no id to dangle. An empty
// or absent `pathTiles` is a feed with no path to break.
export function isConduitCharged(room: RoomDef, pillar: ObjectDef, physics: PhysicsConfig = DEFAULT_PHYSICS, caster?: Caster): boolean {
  const feed = pillar.conduit?.feed;
  if (!feed) return false;
  const source = feed.standingPillarId === undefined ? pillar : room.objects.find((o) => o.id === feed.standingPillarId);
  // The named source's *network potential*, not its stored Charge (changed
  // 2026-08-08 with the capacity model). For a pillar sitting on no network
  // but its own the two are the same number, which is every shipped room; they
  // diverge exactly when a wire joins it to something, which is the point --
  // a Font wired to a Standing Pillar should raise what the pillar reads
  // without the pillar having to physically hold it.
  if (!source || networkPotential(room, source, physics, caster) < feed.threshold) return false;
  return (feed.pathTiles ?? []).every(({ x, y }) => room.objects.some((o) => o.x === x && o.y === y && isConductive(o, physics)));
}

// Whether a gate currently stands open. `open` is the live flag a trigger
// wrote; `defaultOpen` is what the room authored. Reading the two in that
// order is what lets an authored room state its starting position exactly once
// -- there is no "write defaultOpen into open at load" step to forget, and no
// second copy to drift.
export function gateIsOpen(gate: GateDef): boolean {
  return gate.open ?? gate.defaultOpen ?? false;
}

/** Whether this object is a **gate standing in a channel** -- a Sluice Gate.
 *
 * Carrying both clusters is the whole test, in the same spirit as every other
 * "what is this thing" question in this codebase: a belt is whatever carries a
 * `conveyor` cluster, and a sluice is whatever is a gate and part of the water
 * graph at once. There is no `sluice: true` to author, so there is nothing to
 * author *wrongly* -- an object cannot claim to be a sluice while lacking the
 * gate that shuts it or the channel it shuts.
 *
 * Three rules read this, and all three say the same thing from different sides:
 * what a sluice opens is the **channel**, not the doorway.
 *
 * - It stays impassable to the player open or shut (`tileIsFree`).
 * - It carries fluid exactly while open (`isWaterPassable`).
 * - It is never removed from the room when it opens (`checkGateThreshold`) --
 *   an ordinary one-way gate vanishes when solved, and a vanished sluice would
 *   take a tile of channel with it and leave the flow running into a hole. */
export function isSluice(obj: ObjectDef): boolean {
  return obj.gate !== undefined && obj.water !== undefined;
}

// Whether this gate is ever visible in its open state -- true if it starts
// open, or if any trigger can shut it again. False for the only shape that
// existed before triggers did (starts closed, every trigger opens), which is
// why that shape can still be removed outright when it opens rather than left
// standing as an open gate nobody can tell apart from an unsolved one. See
// GateDef for the full argument.
//
// A `mode: 'hold'` trigger counts regardless of its own action, 'toggle'
// aside (see GateTriggerMode): a hold trigger re-derives `open` from its
// condition every settle, so a hold+'open' trigger closes the gate again the
// instant its condition stops being met, exactly as surely as a plain
// 'close'/'toggle' trigger can -- getting this wrong would have
// checkGateThreshold remove the gate outright the first time it opened,
// leaving nothing left for a falling edge to ever re-close.
export function gateShowsOpen(gate: GateDef): boolean {
  if (gate.defaultOpen === true) return true;
  return (gate.triggers ?? []).some((trigger) => trigger.action === 'close' || trigger.action === 'toggle' || trigger.mode === 'hold');
}

// Whether one object a trigger names currently reads as satisfied, ignoring any
// state test or negation -- the object's *own* default question. The whole point
// of one dispatch here rather than a list per condition kind on the gate:
// teaching gates a new kind of condition (a lever that has been pulled, water
// reaching a tile) is a branch here rather than another near-duplicate array on
// GateDef.
export function isGateConditionMet(room: RoomDef, obj: ObjectDef, physics: PhysicsConfig = DEFAULT_PHYSICS, caster?: CasterTile): boolean {
  if (obj.pad) return isPadSatisfied(room, obj, caster);
  if (obj.conduit?.feed) return isConduitCharged(room, obj, physics);
  return false;
}

/** Whether one `GateCondition` currently holds.
 *
 * Three readings, in order: a condition naming a **state** compares that
 * object's `heatState.current`; otherwise the object answers its own default
 * question above; and `negate` inverts whichever was asked. A condition naming
 * an object that is not in the room reads **false** rather than throwing --
 * an object can legitimately leave (a melted barrier, a merged rock), and a gate
 * watching something that is gone is unsatisfied, not broken. A state named on
 * an object with no ladder reads false for the same reason.
 *
 * Note what `negate` does *not* do: it does not make a gate fall closed. That is
 * the trigger's `mode`/`action` question, and the two are orthogonal on purpose
 * -- "hold open while this candle is *not* melted" and "set closed once it is"
 * are different sentences, and both are now sayable. */
export function isConditionMet(room: RoomDef, condition: GateCondition, physics: PhysicsConfig = DEFAULT_PHYSICS, caster?: CasterTile): boolean {
  const obj = room.objects.find((o) => o.id === condition.id);
  const met = !obj ? false : condition.state !== undefined ? obj.heatState?.current === condition.state : isGateConditionMet(room, obj, physics, caster);
  return condition.negate ? !met : met;
}

/** One locus on a charge network: an object, or the caster. */
export type ChargeLocus = { kind: 'object'; obj: ObjectDef } | { kind: 'caster' };

/** The caster as a network locus. Only their Charge is needed -- capacity is 1
 * by definition of the model (see chargeBind.ts's PLAYER_CAPACITY), so their
 * potential *is* their charge. Passed explicitly rather than read off a world,
 * because these are room-level queries and the editor's overlays call them
 * against a room it is not standing in. */
export interface Caster {
  charge: number;
}

/** Everything a diagnostic view wants to know about one object's place in the
 * Charge system, or `undefined` for an object that has none.
 *
 * Exists because "why is this circuit dead" is a question with several possible
 * answers and no way to see any of them: a run of cable draws the same picture
 * whether it is carrying 8 or nothing, a Charged Font resting *beside* a cable
 * rather than *on* it looks identical to one that is connected, and a corroded
 * tile mid-run is the only failure the world currently shows you. This is the
 * whole of what the game's conductivity overlay and any other tool should read,
 * so that no two of them can disagree about what conducts.
 *
 * `conducts` is exactly what the network walk spreads across -- an
 * `isConductive` reading (a run of cable, a Metal Floor plate, a terminal, a
 * pushed Metal Block) or flowing water. Until 2026-08-18 it was a union of
 * that with the narrower `isConductiveFixture` the walk actually used, so the
 * overlay drew a Metal Block as conducting while the walk refused to spread
 * across it; the two now ask one question. `stores` is capacity: whether this
 * locus holds a share of what the network carries, or merely reads it.
 * `potential` is `SumQ / SumC` over its network, the number every reader
 * actually compares against its own threshold. */
export interface ConductionReading {
  conducts: boolean;
  stores: boolean;
  potential: number;
}

/** Whether this object takes part in the Charge system at all -- the filter that
 * keeps a diagnostic view from outlining every crate in the room. */
function inTheChargeSystem(obj: ObjectDef): boolean {
  return (
    obj.conduit !== undefined ||
    obj.breaker !== undefined ||
    obj.chargeBind !== undefined ||
    obj.water !== undefined ||
    (obj.capacity ?? 0) > 0 ||
    obj.properties?.charge !== undefined
  );
}

export function conductionReading(room: RoomDef, obj: ObjectDef, physics: PhysicsConfig = DEFAULT_PHYSICS, caster?: Caster): ConductionReading | undefined {
  if (!inTheChargeSystem(obj)) return undefined;
  const flowing = obj.water ? traceWaterFlow(room) : new Set<string>();
  return {
    conducts: isConductive(obj, physics) || (obj.water !== undefined && flowing.has(`${obj.x},${obj.y}`)),
    stores: (obj.capacity ?? 0) > 0,
    potential: networkPotential(room, obj, physics, caster),
  };
}

/** Whether this object is a **conductive substance** -- something a charge
 * network spreads across by touch rather than by wire. Two qualify, and the
 * union is deliberate: a river running against a metal plate really is in
 * contact with it, so one predicate covers both and their junction rather than
 * two parallel rules that would need a third to join them.
 *
 * - **Unrusted, unbroken metal** -- anything `isConductive`: a Metal Floor
 *   tile, a run of Cabling, a Gate Terminal, a Circuit Breaker with its blade
 *   closed, a Metal Block somebody shoved into the gap. Rust insulates and an
 *   open breaker cuts: either one neither joins a network nor relays past
 *   itself.
 * - **Flowing water** (added 2026-08-08, step 8). Any object carrying a `water`
 *   cluster whose tile is currently in the flow. That one condition already
 *   says everything the design wanted: **dry riverbed does not conduct** (it is
 *   not in the flow), and **frozen water does not conduct** (`isWaterPassable`
 *   drops a frozen channel tile out of the trace) -- which is true of real ice,
 *   a far poorer conductor than liquid water, and means the freeze cast that
 *   redirects a river also opens a circuit. No new field, no new state.
 *
 * **Every conductor, with no fixture-versus-furniture opt-in** (2026-08-18).
 * A `conduit.terrain` flag used to gate the metal half, on the grounds that
 * letting room8's pushable Metal Blocks fuse by adjacency was a content
 * decision about that puzzle -- but it was the *exclusion* that was the content
 * decision, and it made two indistinguishable arrangements of metal behave
 * differently (a block beside a cable conducted nothing; a block beside a plate
 * it was standing on conducted). One conductor type in the entire world carried
 * the exclusion, and the authored `feed.pathTiles` list that worked around it
 * had already been made redundant by the capacity model. See
 * `review/2026-08-18-conduction-unification.md`. */
function isConductiveSubstance(obj: ObjectDef, physics: PhysicsConfig, flowing: ReadonlySet<string>): boolean {
  if (isConductive(obj, physics)) return true;
  // Water only, deliberately (2026-08-09). Flowing lava would otherwise join
  // charge networks for free the moment it existed, purely because it reuses
  // the same cluster -- a real mechanic arriving by accident, which is the
  // thing this codebase keeps getting bitten by. Molten rock does conduct in
  // reality, so this is a decision to be revisited on purpose, not a claim
  // that it cannot.
  return obj.water !== undefined && (obj.water.fluid ?? 'water') === 'water' && flowing.has(`${obj.x},${obj.y}`);
}

/** Every locus electrically connected to `obj`, `obj` itself included.
 *
 * **One walk, three kinds of edge**, which is the whole point of the 2026-08-08
 * model: conduction does not care how two things came to be touching.
 *
 * - **Adjacency** -- two orthogonally-neighbouring conductors (see
 *   `isConductiveSubstance`: unrusted metal, or flowing water).
 * - **Co-location** -- anything sharing a tile with a conductor is touching
 *   it, and joins its network.
 * - **`chargeBind`** -- a wire, undirected (a wire does not care which end was
 *   cast at), reaching any locus at any distance, including the caster when a
 *   link omits its `sourceId`.
 *
 * The flow is recomputed here rather than read off `world.flowingTiles`, which
 * keeps this a **room-level query with no world argument** -- the same reason
 * `Caster` is passed explicitly (the editor's overlays call these against a room
 * it is not standing in). It is not a second source of truth: `traceWaterFlow`
 * is exactly what `recomputeWaterFlow` stores, and it returns an empty set
 * immediately in the fourteen rooms with no water source at all, so the cost is
 * one `find` everywhere it does not apply. */
export function chargeNetwork(room: RoomDef, obj: ObjectDef, physics: PhysicsConfig = DEFAULT_PHYSICS, caster?: Caster): ChargeLocus[] {
  const flowing = traceWaterFlow(room);
  const conducts = (o: ObjectDef) => isConductiveSubstance(o, physics, flowing);
  const byId = new Map(room.objects.map((o) => [o.id, o]));

  const loci: ChargeLocus[] = [{ kind: 'object', obj }];
  const seen = new Set<string>([obj.id]);
  let casterJoined = false;

  const addObject = (o: ObjectDef | undefined) => {
    if (!o || seen.has(o.id)) return;
    seen.add(o.id);
    loci.push({ kind: 'object', obj: o });
  };

  for (let i = 0; i < loci.length; i++) {
    const here = loci[i];
    if (here.kind === 'caster') {
      // A wire naming the caster reaches back the other way too.
      for (const o of room.objects) if (o.chargeBind && o.chargeBind.sourceId === undefined) addObject(o);
      continue;
    }
    const tile = here.obj;
    // Adjacency is contact along an edge: two conductors on neighbouring tiles
    // are touching, so the network spreads between them. Only a conductor
    // relays -- a Wood Block on the next tile is a break in the run, which is
    // what makes swapping one block for another the whole of room8's puzzle.
    if (conducts(tile)) {
      for (const dir of DIRECTIONS) {
        const offset = DIRECTION_OFFSETS[dir];
        addObject(room.objects.find((o) => o.x === tile.x + offset.x && o.y === tile.y + offset.y && conducts(o)));
      }
    }
    // Co-location is contact across the same tile: anything resting on a
    // conductor is touching it and joins its network. This is what
    // `chargeOnTile` used to express as a max() over the tile's occupants,
    // generalized -- room11's Charged Fonts sit
    // on Metal Floor tiles for exactly this reason, and under the capacity model
    // they are the *stores* that give that grid a potential at all, since the
    // tiles themselves hold nothing.
    //
    // **Symmetric, since 2026-08-16.** This loop used to sit inside the
    // `conducts(tile)` branch above, so the edge existed only when walked from
    // the ground upward: starting a traversal at a Metal Floor tile reached the
    // Font standing on it, and starting one at the Font reached nothing at all.
    // A Font wired to two Terminals reported a network of one locus and a
    // potential of 8 -- its own -- while the Terminals on the very same grid
    // correctly reported 8/3.
    //
    // Nothing was wrong yet, because every caller happened to start from the
    // conductor (`isConduitCharged` from a feed's own object or its named
    // `standingPillarId`). The fix is provably behaviour-preserving for those:
    // from the conductor's side the rider was already added, and the rider now
    // only walks back to a tile that is already `seen`. What it changes is the
    // question this file could not previously answer -- *what is drawing on
    // me* -- which is the whole of `chargeSource.ts`'s ladder, and the
    // conductivity overlay's reading at a Font.
    for (const o of room.objects) {
      if (o === tile || o.x !== tile.x || o.y !== tile.y) continue;
      if (conducts(tile) || conducts(o)) addObject(o);
    }
    // Outgoing wire: this object names its partner.
    if (tile.chargeBind) {
      const sourceId = tile.chargeBind.sourceId;
      if (sourceId === undefined) {
        if (caster && !casterJoined) {
          casterJoined = true;
          loci.push({ kind: 'caster' });
        }
      } else addObject(byId.get(sourceId));
    }
    // Incoming wires: anything naming this object.
    for (const o of room.objects) if (o.chargeBind?.sourceId === tile.id) addObject(o);
  }
  return loci;
}

/** What a charge network settles at: `V = SumQ / SumC` over every locus on it
 * (see `04_Physics_Engine.md`'s Conservation of Charge section).
 *
 * This is what every reader of "is there charge here" now asks, and it is why
 * a capacity-0 conductor is useful rather than inert: it holds nothing, but it
 * *reads* the network's potential, so a wire or a floor grid carries a source's
 * full reading to the far end without draining it. A component with no capacity
 * anywhere has no potential to speak of and reads 0 -- there is nowhere in it
 * for charge to be.
 *
 * Note this replaced a *boolean reachability* rule on 2026-08-08 ("any one
 * member at or above threshold lights the whole network"). The two agree in
 * every shipped room -- with capacity-0 conductors and capacity-1 sources, V
 * stays at the source's own level however much conductor is attached, which is
 * exactly the non-diluting behaviour a wire should have -- but they are not the
 * same rule, and they part company as soon as a second *store* joins a network
 * and divides its potential. */
export function networkPotential(room: RoomDef, obj: ObjectDef, physics: PhysicsConfig = DEFAULT_PHYSICS, caster?: Caster): number {
  let totalCharge = 0;
  let totalCapacity = 0;
  let loci = 0;
  for (const locus of chargeNetwork(room, obj, physics, caster)) {
    loci++;
    if (locus.kind === 'caster') {
      totalCharge += caster!.charge;
      totalCapacity += 1;
    } else {
      totalCharge += locus.obj.properties?.charge ?? 0;
      totalCapacity += locus.obj.capacity ?? 0;
    }
  }
  // No store anywhere on the network. Capacity 0 means "negligible *relative to
  // the stores present*", not "physically impossible to charge" -- a real
  // conductor always has some capacitance -- so when there are no stores at
  // all, nothing is negligible and every locus counts as its own unit. That is
  // exactly the equal-share rule this model replaced, kept alive in the one
  // case the model does not describe, which is what makes charging a bare floor
  // grid (or a wire between two capacity-less props) behave as it always did.
  if (totalCapacity === 0) return loci === 0 ? 0 : totalCharge / loci;
  return totalCharge / totalCapacity;
}

// Whether a hazard object (a Metal Floor tile, `conduit.shockDamage` set) would
// arc into whoever touched it right now: it is still conductive itself, and the
// *potential difference* across that contact is at least
// `physics.metal.floorLiveCharge`. Rusting a tile both removes it from every
// neighbour's network (see chargeNetwork) and, checked here directly, stops it
// personally reading live even while sitting inside a still-charged one -- the
// same "insulates, does not just look different" rule metal-rust already has.
//
// **A difference, not a level** (2026-08-08, step 7 of the capacity rebuild).
// Called with no `caster` this is exactly the old rule -- an unspecified
// visitor is a neutral one, so the difference is the network's own potential --
// which is what the editor overlay and the renderer want: "would this tile
// shock someone walking in off the street". Called *with* one it is symmetric,
// and two things the design has wanted for a while fall straight out of the
// arithmetic with no extra rule:
//
// - **Carrying Charge near a conductor is dangerous** (parked in
//   `07_Open_Questions.md` since 2026-07-27). A neutral floor under a charged
//   caster reads exactly as live as a charged floor under a neutral one,
//   because it is the same |V_net - V_caster|.
// - **Joining a network is how you cross it safely.** A caster wired in with
//   Bind·Charge is already a locus of this very network, so `networkPotential`
//   includes them and they are already sitting at V -- the difference is zero
//   and this reads false, with no special case for "is the caster bound".
export function isConduitLive(room: RoomDef, obj: ObjectDef, physics: PhysicsConfig = DEFAULT_PHYSICS, caster?: Caster): boolean {
  if (!isConductive(obj, physics)) return false;
  const gap = networkPotential(room, obj, physics, caster) - (caster?.charge ?? 0);
  return Math.abs(gap) >= physics.metal.floorLiveCharge;
}

// Whether water can trace through a tile: a source always can, a bridge
// (room9's Aqueduct) only once restored, a reservoir terminus once reachable,
// and an ordinary channel tile only while it isn't frozen. A tile with none of
// those isn't part of the water graph at all; plain floor never carries
// water just because it's open, the same way a Chasm blocks the player
// without needing to be "water-aware" itself.
/** Whether this tile actually has fluid in it right now -- which is a different
 * question from whether it is on the transport graph, and the one every
 * *physical* rule wants.
 *
 * A frozen tile holds no liquid: it is solid, out of the flow and out of the
 * heat circuit at both ends (which is what keeps room9's freeze-to-redirect
 * permanent). An unfrozen **body** (`water.body`, a lava pool) always holds its
 * fluid -- nothing feeds it and nothing needs to. An unfrozen **channel** holds
 * fluid exactly while a trace reaches it.
 *
 * Introduced 2026-08-09 to end a split of my own making: a pool of lava carried
 * neither `water` nor a place on any trace, so it could not freeze and did not
 * conduct, while a channel tile three bays away did both. Two objects that are
 * visibly the same substance behaving differently is the exact bug this
 * codebase keeps finding, and I had just written a fresh one. */
export function holdsFluid(obj: ObjectDef, flowing: ReadonlySet<string>): boolean {
  if (!obj.water || obj.water.frozen) return false;
  return !!obj.water.body || flowing.has(`${obj.x},${obj.y}`);
}

export function isWaterPassable(room: RoomDef, x: number, y: number, fluid: FluidId = 'water'): boolean {
  // Scoped to one fluid (2026-08-09). A channel carries water or lava, never
  // both, so a trace only ever steps onto tiles of its own kind -- which is
  // what lets the two share a room without either flowing down the other's
  // trench.
  const objs = room.objects.filter((o) => o.x === x && o.y === y && o.water !== undefined && (o.water.fluid ?? 'water') === fluid);
  if (objs.some((o) => o.water?.source)) return true;
  const bridge = objs.find((o) => o.water?.bridge);
  if (bridge) {
    // Deliberately not gated on isWalkable -- a bridge is a fixed, non-walkable
    // structure (see WaterDef.bridge), and water's ability to trace through it
    // is entirely independent of whether the player can stand on that tile.
    //
    // **A bridge carries fluid while everything that can shut it is open**
    // (generalized 2026-08-17, when the Sluice Gate arrived). Two mechanisms can
    // shut one and they are checked the same way, by asking each what it already
    // knows rather than by the bridge carrying a state of its own:
    //
    // Both read a *persisted* flag rather than the live sprite's texture -- the
    // sprite is rebuilt from `obj.texture` on every room entry (see
    // enterRoom/createObjectSprite), so checking that would forget the Aqueduct
    // was ever restored, or the sluice ever opened, the moment the player left
    // and came back.
    if (bridge.gate && !gateIsOpen(bridge.gate)) return false;
    if (bridge.barrier?.transformsInto && !bridge.barrier.transformed) return false;
    // A bridge with nothing to shut it is simply an open trough. Nothing
    // authored is one today; the alternative is a bridge that silently never
    // carries anything, which is the worse of the two failure modes to hand an
    // author who has not yet wired up the thing that opens it.
    return true;
  }
  // There is deliberately no branch here for `water.powersReservoir` (removed
  // 2026-08-18). One used to say that a driven machine's tile is passable "once
  // it's reachable at all", and then deferred to `canObjectEnter` -- which for
  // room9's Water Wheel, a non-walkable object, meant the wheel **dammed the
  // channel it was supposed to be turned by**, and only let water past once
  // enough Entropy had collapsed it into rubble. A wheel that generates power
  // exclusively while smashed, and stops a river dead while whole, is backwards
  // twice over.
  //
  // Being *driven* by water and being *passable* to it are two different claims,
  // and only the second belongs here. A wheel says the second the same way the
  // Aqueduct does, by being a `water.bridge` -- a structure carrying a fluid,
  // with nothing that can shut it, which is the "simply an open trough" case
  // `WaterDef.bridge` already describes.
  const channel = objs.find((o) => o.water?.flowingTexture);
  if (!channel) return false;
  // Real, persisted state (see WaterDef.frozen's own doc comment), not a
  // live re-derivation from Energy -- checkWaterFreeze (heatState.ts) is
  // what actually watches the freeze/melt threshold pair and flips this.
  return !channel.water?.frozen && canObjectEnter(room, x, y);
}

// A directed trace, not a symmetric flood-fill: water keeps going in
// whatever direction it's already flowing for as long as the next tile in
// that direction is passable, and only fans out to check the other
// directions once that fails -- the same "follow the channel, only turn at
// a dead end" rule a real diverted stream follows (see room9's dev
// signpost). A symmetric flood-fill would light up every branch
// simultaneously regardless of whether the player had actually redirected
// anything, which defeats the puzzle.
export function traceWaterFlow(room: RoomDef, fluid: FluidId = 'water'): Set<string> {
  const flowing = new Set<string>();
  const source = room.objects.find((o) => o.water?.source && (o.water.fluid ?? 'water') === fluid);
  if (!source) return flowing;
  const key = (x: number, y: number) => `${x},${y}`;
  flowing.add(key(source.x, source.y));
  let cx = source.x;
  let cy = source.y;
  let dir: Facing | undefined;
  const maxSteps = room.cols * room.rows;
  for (let step = 0; step < maxSteps; step++) {
    const candidate = (d: Facing) => {
      const offset = DIRECTION_OFFSETS[d];
      return { x: cx + offset.x, y: cy + offset.y };
    };
    let next = dir && isWaterPassable(room, candidate(dir).x, candidate(dir).y, fluid) && !flowing.has(key(candidate(dir).x, candidate(dir).y)) ? dir : undefined;
    if (!next) {
      next = DIRECTIONS.find((d) => {
        const pos = candidate(d);
        return isWaterPassable(room, pos.x, pos.y, fluid) && !flowing.has(key(pos.x, pos.y));
      });
    }
    if (!next) break;
    const pos = candidate(next);
    cx = pos.x;
    cy = pos.y;
    dir = next;
    flowing.add(key(cx, cy));
  }
  return flowing;
}

/** Whether an object can be marked as a Transfer/Bind source.
 *
 * `properties` makes it a real Transfer locus; `pushable` makes it a realistic
 * `Bind`·`Momentum` one, now that marking feeds that too. Either is reason
 * enough to let it be marked.
 *
 * Here rather than inline in `GameScene.pushFacedTarget` (where it was, and
 * where it was the only statement of the rule) because the analysis search has
 * to ask the same question: marking is a *move* there, and one the caster can
 * only make while facing something the game would let them mark. Two
 * formulations of "can this be marked" would let the search propose a mark the
 * player cannot make, which is the false-positive class `search.ts` exists to
 * rule out. */
export function isLinkable(obj: ObjectDef): boolean {
  return !!obj.properties || !!obj.pushable;
}
